import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock, User, MessageSquare, Hash, Share2, ExternalLink } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';
import { Layout } from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import { useBlogs } from '@/hooks/useBlogs';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { normalizeMarkdown } from '@/lib/markdown-utils';

export default function BlogPostDetail() {
  const { slug } = useParams<{ slug: string }>();
  const { getBlogBySlug, blogs } = useBlogs();
  const { data: blog, isLoading, isError } = getBlogBySlug(slug || '');

  // SEO meta tags
  useEffect(() => {
    if (!blog) return;
    const title = blog.seo_title || blog.title;
    const description = blog.seo_description || blog.excerpt;
    document.title = `${title} | NexQR Blog`;

    const setMeta = (name: string, content: string, isProperty?: boolean) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('description', description);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', 'article', true);
    setMeta('og:url', window.location.href, true);
    if (blog.image_url) setMeta('og:image', blog.image_url, true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (blog.image_url) setMeta('twitter:image', blog.image_url);

    return () => { document.title = 'NexQR'; };
  }, [blog]);

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB] dark:bg-zinc-950">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">Loading blog post...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (isError || !blog) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB] dark:bg-zinc-950">
          <div className="text-center">
            <p className="text-destructive">Blog post not found. Please try another article.</p>
            <Link to="/blog" className="inline-block mt-4 text-primary hover:underline">
              ← Back to Blog
            </Link>
          </div>
        </div>
      </Layout>
    );
  }

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Sharing failed', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-[#F8F9FB] dark:bg-zinc-950">
        <section className="bg-white dark:bg-zinc-900 border-b border-zinc-200 dark:border-zinc-800 pt-12 pb-20">
          <div className="max-w-7xl mx-auto px-4">
            <Link to="/blog" className="inline-flex items-center text-sm font-bold text-primary mb-8 hover:gap-2 transition-all">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Blog
            </Link>
            
            <div className="max-w-4xl">
              <div className="flex flex-wrap gap-2 mb-6">
                {blog.tags.map((tag, index) => (
                  <Badge key={index} className="bg-primary/10 text-primary hover:bg-primary/20 border-0 px-4 py-1">
                    {tag}
                  </Badge>
                ))}
              </div>
              <h1 className="text-4xl md:text-6xl font-black text-zinc-900 dark:text-white tracking-tight leading-[1.1] mb-8">
                {blog.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-500 dark:text-zinc-400 font-bold uppercase tracking-widest">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                    <User className="w-5 h-5" />
                  </div>
                  <span>By {blog.author}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>{new Date(blog.published_at || blog.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>{blog.read_time} min read</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row gap-16">
            <BlogSidebar posts={blogs as any[]} />

            <main className="flex-1 min-w-0">
              <div className="bg-white dark:bg-zinc-900 rounded-[2.5rem] p-8 md:p-12 border border-zinc-200 dark:border-zinc-800 shadow-sm">
                {/* Featured Image */}
                {blog.image_url && (
                  <div className="mb-10 -mt-2">
                    <img
                      src={blog.image_url}
                      alt={blog.title}
                      className="w-full h-auto max-h-[480px] object-cover rounded-[2rem] shadow-xl"
                    />
                  </div>
                )}

                <div className="prose prose-lg md:prose-xl dark:prose-invert max-w-none 
                  prose-headings:font-black prose-headings:tracking-tight prose-headings:text-zinc-900 dark:prose-headings:text-white
                  prose-h1:text-3xl prose-h1:mt-10 prose-h1:mb-4
                  prose-h2:text-2xl prose-h2:mt-8 prose-h2:mb-3 prose-h2:border-b prose-h2:border-zinc-200 prose-h2:pb-2 dark:prose-h2:border-zinc-700
                  prose-h3:text-xl prose-h3:mt-6 prose-h3:mb-2
                  prose-p:text-zinc-600 dark:prose-p:text-zinc-400 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-primary prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                  prose-img:rounded-[2rem] prose-img:shadow-xl prose-img:my-8
                  prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:bg-primary/5 prose-blockquote:p-6 prose-blockquote:rounded-r-2xl prose-blockquote:italic prose-blockquote:font-medium prose-blockquote:my-6
                  prose-ul:list-disc prose-ul:ml-6 prose-ul:space-y-2 prose-ul:my-4
                  prose-ol:list-decimal prose-ol:ml-6 prose-ol:space-y-2 prose-ol:my-4
                  prose-li:ml-2 prose-li:text-zinc-600 dark:prose-li:text-zinc-400
                  prose-code:bg-zinc-100 dark:prose-code:bg-zinc-800 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm
                  prose-pre:bg-zinc-900 prose-pre:rounded-2xl prose-pre:p-6
                  prose-hr:my-8 prose-hr:border-zinc-200 dark:prose-hr:border-zinc-700
                ">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
                    {normalizeMarkdown(blog.content)}
                  </ReactMarkdown>
                </div>

                <Separator className="my-12" />

                <div className="flex flex-col md:flex-row items-center justify-between gap-8 bg-zinc-50 dark:bg-zinc-800/50 p-8 rounded-[2rem]">
                  <div className="flex items-center gap-4">
                    <Button 
                      onClick={handleShare}
                      variant="outline" 
                      className="rounded-full font-bold flex items-center gap-2"
                    >
                      <Share2 className="w-4 h-4" />
                      Share Article
                    </Button>
                    <Button 
                      variant="outline" 
                      className="rounded-full font-bold flex items-center gap-2"
                      onClick={() => {
                        navigator.clipboard.writeText(window.location.href);
                        toast.success('Link copied to clipboard!');
                      }}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Copy Link
                    </Button>
                  </div>
                  <div className="flex items-center gap-4">
                    <Button variant="outline" className="rounded-full font-bold">Report Post</Button>
                    <Button className="rounded-full bg-primary font-black px-8">Join Discussion</Button>
                  </div>
                </div>
              </div>
            </main>
          </div>
        </div>
      </div>
    </Layout>
  );
}